import {
  Card,
  CardContent,
  Divider,
  Grid,
  Typography,
} from '@material-ui/core';
import { grey } from '@material-ui/core/colors';
import { makeStyles } from '@material-ui/core/styles';
import clsx from 'clsx';
import { format } from 'date-fns';
import React, { useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Trip } from 'src/.gen/graphql';
import useDoubleClick from 'src/shared/hooks/useDoubleClick';
import {
  getActualEstimatedDurationInMinutes,
  getTotalStopWaypoints,
  parseWaypoint,
} from 'src/shared/utils/addressUtils';
import { convertMinutesToFormattedTime } from 'src/shared/utils/conversions';
import { shorten } from 'src/shared/utils/stringUtils';
import AddressComponent from 'src/shared/components/AddressComponent';
import useBreakpoints from 'src/shared/hooks/useBreakpoints';
import { userAtom } from 'src/authentication/atoms/AuthState';
import { useRecoilValue } from 'recoil';
import NextTripInfoItem from '../NextTrip/NextTripInfoItem';
import TripStatusBox from './TripStatusBox';

const useStyles = makeStyles((theme) => ({
  mainContainer: {
    flex: 1,
  },
  withBottomGutter: {
    marginBottom: theme.spacing(1),
  },
  boxSpacer: {
    height: '100%',
    marginTop: 'auto',
    marginBottom: 'auto',
  },
  cardContainer: {
    display: 'flex',
    minHeight: '10rem',
    maxHeight: '14rem',
    cursor: 'pointer',
    [theme.breakpoints.down('sm')]: {
      maxHeight: 'unset',
    },
  },
  cardContent: {
    width: '100%',
  },
  content: {
    display: 'flex',
    height: '100%',
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
    },
  },
  selectedCard: {
    backgroundColor: grey[300],
  },
  leftContainer: {
    flex: 6,
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    padding: '0rem .5rem',
    minWidth: '130px',
  },
  rightContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    flexDirection: 'column',
    flex: 10,
    paddingLeft: '0.5rem',
    [theme.breakpoints.down('lg')]: {
      maxWidth: '220px',
      '& p': {
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      },
    },
    [theme.breakpoints.down('sm')]: {
      marginTop: theme.spacing(2),
    },
  },
  statusContainer: {
    flex: 1,
    display: 'flex',
    alignItems: 'flex-end',
  },
  titlePassanger: {
    fontWeight: theme.typography.fontWeightBold,
    color: theme.palette.textGray.dark,
  },
  description: {
    fontWeight: theme.typography.fontWeightMedium,
  },
  referenceNumber: {
    fontWeight: theme.typography.fontWeightLight,
    float: 'left',
  },
  sharedTripLabel: {
    color: theme.palette.primary.main,
    fontSize: '0.8rem',
  },
}));

interface TripListTileProps {
  trip: Trip;
  selectedTripId?: string | undefined;
  elevation?: number;
  withBottomGutter?: boolean;
  variant?: 'outlined' | 'elevation';
  onClick?: () => void;
}

const primaryDataTestId = 'trip_list_tile_card_content';

const TripListTile: React.FC<TripListTileProps> = ({
  trip,
  onClick,
  selectedTripId,
  withBottomGutter = false,
  elevation = 1,
  variant,
}) => {
  const cardRef = useRef();
  const classes = useStyles();
  const user = useRecoilValue(userAtom);
  const iOwnThisTrip = user?.accountId === trip?.accountId;
  const navigate = useNavigate();
  const { sm } = useBreakpoints();
  useDoubleClick({
    onSingleClick: () => {
      if (onClick) {
        if (sm) {
          navigate(`/trips/${trip.id}`);
        } else {
          onClick();
        }
      } else {
        navigate(`/trips/${trip.id}`);
      }
    },
    onDoubleClick: () => {
      navigate(`/trips/${trip.id}`);
    },
    ref: cardRef,
    latency: 250,
  });
  const pickupAddress = parseWaypoint(trip?.pickupWaypoint);
  const dropoffAddress = parseWaypoint(
    trip?.dropoffWaypoint,
    trip?.overrideDropoffWaypointDetails,
  );
  return (
    <Card
      data-testid="trip_list_tile_card"
      variant={variant}
      ref={cardRef}
      className={clsx(
        classes.cardContainer,
        trip?.id === selectedTripId && classes.selectedCard,
        withBottomGutter && classes.withBottomGutter,
      )}
      elevation={elevation}
    >
      <CardContent
        className={classes.cardContent}
        data-testid={primaryDataTestId}
      >
        <Grid container direction="row" spacing={3}>
          <Grid item xs={12}>
            <div className={classes.content}>
              <div
                className={classes.leftContainer}
                data-testid={`${primaryDataTestId}_left_container`}
              >
                <Typography
                  variant="body2"
                  color="primary"
                  className={classes.titlePassanger}
                  data-testid={`${primaryDataTestId}_left_container_name`}
                >
                  {shorten(trip?.passengerContact?.name ?? '            ', 15)}
                </Typography>
                {trip?.isShared && (
                  <Typography
                    variant="body2"
                    className={classes.sharedTripLabel}
                  >
                    {`${iOwnThisTrip ? 'Shared Trip' : 'Received Trip'}`}
                  </Typography>
                )}
                <Typography
                  variant="caption"
                  className={classes.description}
                  data-testid={`${primaryDataTestId}_left_container_trip_number`}
                >
                  {`#${trip?.tripNumber ?? 'No trip number'}`}
                </Typography>
                <Typography
                  variant="caption"
                  className={classes.description}
                  data-testid={`${primaryDataTestId}_left_container_pickup_time`}
                >
                  {`${format(
                    new Date(trip?.pickupTime),
                    'MM/dd/yyyy h:mm aaa',
                  )}`}
                </Typography>
                <Typography
                  variant="caption"
                  className={classes.description}
                  data-testid={`${primaryDataTestId}_left_container_vehicle`}
                >
                  {trip?.vehicle?.year} {trip?.vehicle?.model?.make?.name}{' '}
                  {trip?.vehicle?.model?.name}
                </Typography>
                <Typography
                  variant="caption"
                  className={classes.description}
                  data-testid={`${primaryDataTestId}_left_container_duration`}
                >
                  Duration
                  <br />
                  {convertMinutesToFormattedTime(
                    getActualEstimatedDurationInMinutes(trip),
                  )}
                </Typography>
                <div className={classes.boxSpacer} />
                <div
                  className={classes.statusContainer}
                  data-testid={`${primaryDataTestId}_left_container_status_box`}
                >
                  <TripStatusBox
                    status={trip?.status}
                    sharedStatus={trip?.sharedTrip?.status}
                    iOwnThisTrip={iOwnThisTrip}
                  />
                </div>
              </div>
              <Divider orientation="vertical" flexItem />
              <div
                className={classes.rightContainer}
                data-testid={`${primaryDataTestId}_right_container`}
              >
                <NextTripInfoItem
                  label="Pickup"
                  value={<AddressComponent address={pickupAddress} />}
                />
                <NextTripInfoItem
                  label="Stops"
                  value={`${getTotalStopWaypoints(trip?.waypoints)}`}
                />
                <NextTripInfoItem
                  label="Dropoff"
                  value={<AddressComponent address={dropoffAddress} />}
                />
              </div>
            </div>
          </Grid>
          {trip?.referenceNumber ? (
            <Grid item xs={12}>
              <Typography
                variant="caption"
                color="textSecondary"
                className={classes.referenceNumber}
                data-testid={`${primaryDataTestId}_left_container_reference_number`}
              >
                {`Ref #: ${trip?.referenceNumber}`}
              </Typography>
            </Grid>
          ) : null}
        </Grid>
      </CardContent>
    </Card>
  );
};

export default TripListTile;
